import React, { useState } from 'react';
import { Card, Steps } from 'antd';
import { AddTrackedWebsitesParams } from '../types';
import { getAvailableLanguagesForLocation } from '../utils';
import { normalizeUrl } from '../../shared/utils';
import { extractKeywords } from '../../utils/keywords';
import { useNavigate } from "react-router-dom";
import { useFetchLocationsAndLanguagesQuery, useFetchMonitorRegionsQuery, useSendOnboardingEventMutation } from '../service';
import useGetPlanSettings from '../../sales/hooks/useGetPlanSettings';
import { useFetchTrackedKeywordsCountQuery } from '../../trackedKeywords/service';
import OnboardingStartScreen from './steps/OnboardingStartScreen';
import InputDomainStep from './steps/InputDomainStep';
import SetLocationsStep from './steps/SetLocationsStep';
import SetCompetitorsStep, { Competitor } from './steps/SetCompetitorsStep';
import FinishedScreen from './steps/FinishedScreen';
import SetKeywordsStep from './steps/SetKeywordsStep';
import { extractDomainAndHomepage } from '../../deepAnalysis/utils';
import './AddTrackedWebsiteWizard.less';


const { Step } = Steps;
const wizardSteps = ['Website', 'Location', 'Competitors', 'Keywords'];


type Props = {
    fromOnboarding: boolean;
    onAddTrackedWebsite: (args: AddTrackedWebsitesParams, keywords: string[]) => void;
};


const initialState = {
    showStartScreen: false,
    currentStep: 0,
    domain: '',
    homepageUrl: '',
    targetLocationName: 'United States',
    targetLanguageCode: 'en',
    mainMonitoringRegion: 'us-east-1',
    additionalMonitoringRegions: [] as string[],
    competitors: [] as Competitor[],
    keywords: '',
};

export default function AddTrackedWebsiteWizard({ fromOnboarding, onAddTrackedWebsite }: Props) {
    const [state, setState] = useState({ ...initialState, showStartScreen: fromOnboarding });

    const navigate = useNavigate();
    const [sendOnboardingEvent] = useSendOnboardingEventMutation();
    const { maxCompetitors, maxKeywords, maxRegions } = useGetPlanSettings();
    const { data: trackedKeywordsData } = useFetchTrackedKeywordsCountQuery();
    const { data: monitorRegionsData } = useFetchMonitorRegionsQuery();
    const { data: locationsAndLanguages } = useFetchLocationsAndLanguagesQuery();

    const handleSubmit = () => {
        const normalizedCompetitors = state.competitors
            .filter((competitor) => competitor.domain && !['https://', 'http://'].includes(competitor.domain))
            .map((competitor) => normalizeUrl(competitor.domain, competitor.urlSchema));

        const uniqueCompetitors = normalizedCompetitors.filter(
            (competitorUrl, index) => normalizedCompetitors.indexOf(competitorUrl) === index && competitorUrl !== state.homepageUrl
        );

        const allRegions = state.mainMonitoringRegion ? [state.mainMonitoringRegion, ...state.additionalMonitoringRegions] : [];
        const keywords = extractKeywords(state.keywords);

        setState({ ...state, currentStep: state.currentStep + 1 });

        onAddTrackedWebsite(
            {
                domain: state.domain,
                homepage: state.homepageUrl,
                settings: {
                    location_name: state.targetLocationName,
                    language_code: state.targetLanguageCode,
                    competitors: uniqueCompetitors,
                    regions: allRegions,
                },
            },
            keywords
        );
    };

    const onSkipOnboarding = async () => {
        try {
            sendOnboardingEvent({ event_type: 'skip_onboarding' }).unwrap();
            navigate('/dashboard');
        } catch (error) { }
    };

    const handleStartOnboarding = () => {
        setState({ ...state, showStartScreen: false });
    };

    const handlePreviousStep = () => {
        setState({ ...state, currentStep: state.currentStep - 1 });
    };
    const handleNextStep = () => {
        setState({ ...state, currentStep: state.currentStep + 1 });
    };

    const handleDomainChange = (inputDomain: string) => {
        const { domain, homepage, error } = extractDomainAndHomepage(inputDomain);
        setState({ ...state, domain: !error && domain ? domain : inputDomain, homepageUrl: !error && homepage ? homepage : '' });
    };

    const handleMainMonitoringRegionChange = (region: string) => {
        setState({ ...state, mainMonitoringRegion: region, additionalMonitoringRegions: [] });
    };

    const handleAdditionalMonitoringRegionsChange = (regions: string[]) => {
        setState({ ...state, additionalMonitoringRegions: regions });
    };

    const handleTargetLocationChange = (location: string) => {
        let languageCode = '';

        if (locationsAndLanguages) {
            languageCode = getAvailableLanguagesForLocation(location, locationsAndLanguages)[0].language_code;
        }

        setState({ ...state, targetLocationName: location, targetLanguageCode: languageCode });
    };

    const handleTargetLanguageChange = (languageCode: string) => {
        setState({ ...state, targetLanguageCode: languageCode });
    };

    const handleCompetitorsChange = (competitors: Competitor[]) => {
        setState({ ...state, competitors });
    };

    const handleKeywordsChange = (keywords: string) => {
        setState({ ...state, keywords });
    };

    return (
        <div className="add-tracked-website-wizard">
            <Card
                bordered={false}
                className="wizard-card"
            >
                {state.showStartScreen ? (
                    <OnboardingStartScreen onStartOnboarding={handleStartOnboarding} />
                ) : (
                    <>
                        <Steps className="tracked-website-wizard-steps" current={state.currentStep}>
                            {wizardSteps.map((step) => (
                                <Step
                                    key={step}
                                    title={step}
                                />
                            ))}
                        </Steps>
                        {state.currentStep === 0 && (
                            <InputDomainStep
                                domain={state.domain}
                                fromOnboarding={fromOnboarding}
                                onDomainChange={handleDomainChange}
                                onSkipOnboarding={onSkipOnboarding}
                                onFinishThisStep={handleNextStep}
                            />
                        )}

                        {state.currentStep === 1 && (
                            <SetLocationsStep
                                selectedLocationName={state.targetLocationName}
                                selectedLanguageCode={state.targetLanguageCode}
                                selectedMainRegion={state.mainMonitoringRegion}
                                selectedAdditionalRegions={state.additionalMonitoringRegions}
                                selectedUrl={state.homepageUrl}
                                locationsAndLanguages={locationsAndLanguages ? locationsAndLanguages : []}
                                maxRegions={maxRegions ? maxRegions : 1}
                                monitorRegions={monitorRegionsData ? monitorRegionsData.regions : {}}
                                monitorRegionsOrder={monitorRegionsData ? monitorRegionsData.order : []}
                                onPreviousStep={handlePreviousStep}
                                onMainRegionChange={handleMainMonitoringRegionChange}
                                onAdditionalRegionsChange={handleAdditionalMonitoringRegionsChange}
                                onLocationChange={handleTargetLocationChange}
                                onLanguageChange={handleTargetLanguageChange}
                                onFinishThisStep={handleNextStep}
                            />
                        )}

                        {state.currentStep === 2 && (
                            <SetCompetitorsStep
                                domain={state.domain}
                                competitors={state.competitors}
                                maxCompetitors={maxCompetitors ? maxCompetitors : 1}
                                onPreviousStep={handlePreviousStep}
                                onCompetitorsChange={handleCompetitorsChange}
                                onFinishThisStep={handleNextStep}
                            />
                        )}

                        {state.currentStep === 3 && (
                            <SetKeywordsStep
                                domain={state.domain}
                                keywords={state.keywords}
                                maxKeywords={maxKeywords ? maxKeywords : 0}
                                allowedKeywords={trackedKeywordsData && maxKeywords ? maxKeywords - trackedKeywordsData.keywords_no : 0}
                                onPreviousStep={handlePreviousStep}
                                onKeywordsChange={handleKeywordsChange}
                                onFinishThisStep={() => {
                                    handleNextStep();
                                    handleSubmit();
                                }}
                            />
                        )}

                        {state.currentStep === 4 && <FinishedScreen />}
                    </>
                )}
            </Card>
        </div>
    );
}
